/**
 * What the public site says about each game.
 *
 * Kept apart from the page components because every page needs some slice of
 * it — the home grid, the per-game page, How to Play, and Media all read the
 * same record, so the site cannot contradict itself about how a game plays.
 *
 * The rules summaries below are the rulebook's, not the engine's guesses:
 * Cryptid Files from `RULEBOOK.md`, Fish Tales from `FISH-TALES.md`,
 * War Rigs from `Arc_ War Rigs Current.txt` — the 28 September rulebook, which
 * is the printing the app deals.
 * If a rule changes, change it here too — this is the copy players read before
 * they ever reach the app.
 */

export type GameStatus = "live" | "soon";

export interface GameCard {
  src: string;
  alt: string;
}

export interface Game {
  /** URL slug, also the route: /cryptid-files */
  slug: string;
  name: string;
  /** One line, for the game grid and meta descriptions. */
  tagline: string;
  /** Two or three sentences, for the game's own page. */
  blurb: string;
  players: string;
  length: string;
  /**
   * Three words up the corner of the home grid's card, in the order you do
   * them. Not a tagline — a tagline is a sentence, and the card already has
   * one of those underneath.
   */
  kicker: [string, string, string];
  /** The genre line in the card's meta row: what kind of evening this is. */
  tag: string;
  /**
   * `live` prints a Play button into app.neoncrowgames.com. `soon` prints the
   * newsletter instead.
   *
   * THIS IS A CLAIM ABOUT PRODUCTION, not about the repo. A game is only
   * `live` once its module is in the bundle app.neoncrowgames.com serves.
   * Check before flipping one:
   *
   *   curl -s https://app.neoncrowgames.com | grep -oE "assets/index-[^\"]+\.js"
   *   curl -s https://app.neoncrowgames.com/assets/index-<hash>.js | grep -c "<Game Name>"
   */
  status: GameStatus;
  /** Shown under the status pill when `soon`. */
  statusNote?: string;
  cover: string;
  fan: string;
  /** Accent used for the game's headings and pills. */
  accent: string;
  /** The deck list, as a player would want it: what is in the box. */
  components: { count: string; label: string; detail: string }[];
  /** A turn, in order. */
  turn: { step: string; detail: string }[];
  /** How the game ends and who wins. */
  victory: string[];
  /** Real card faces, built by `scripts/build-website-art.py`. */
  cards: GameCard[];
}

export const GAMES: Game[] = [
  {
    slug: "cryptid-files",
    name: "Cryptid Files",
    tagline: "Chase the monster. Gather the evidence. Publish before you get scooped.",
    blurb:
      "An investigative journalism card game. Five cryptids sit face up on the table and " +
      "every one of them wants three specific kinds of evidence — a photograph, a cast of " +
      "the tracks, a voice on tape. Collect the set and you publish the story and sell the " +
      "newspapers. Hesitate and a rival prints it first.",
    players: "2–4 players",
    length: "30–45 minutes",
    kicker: ["Investigate", "Document", "Publish"],
    tag: "Deduction",
    status: "live",
    cover: "/cryptid-files-cover.webp",
    fan: "/cryptid-files-fan.webp",
    accent: "#00ffff",
    components: [
      {
        count: "20",
        label: "Cryptids",
        detail:
          "From Bigfoot and Mothman to the Dover Demon. Each names the three kinds of " +
          "evidence that will print it, and is worth 2 to 5 newspapers.",
      },
      {
        count: "60",
        label: "Evidence",
        detail:
          "Eight kinds — eyewitness reports, bio samples, photographs, tracks, sensor " +
          "data, anonymous tips, physical traces and audio recordings.",
      },
      {
        count: "30",
        label: "Action cards",
        detail:
          "Scoops, hoaxes, fact checks and stakeouts. Every one of them has live rules " +
          "text; none of it is decoration.",
      },
    ],
    turn: [
      { step: "Refresh", detail: "An empty deck is rebuilt from its own discard pile." },
      { step: "Draw", detail: "One card, from either the Evidence deck or the Action deck." },
      {
        step: "Work",
        detail:
          "Publish a cryptid by spending the three kinds of evidence it names, or " +
          "Investigate to dig for more. One or the other — never both.",
      },
      { step: "Refresh the field", detail: "A published cryptid leaves a hole; a new one fills it." },
      { step: "Discard", detail: "Down to a hand of seven." },
    ],
    victory: [
      "The game ends the moment the table has published ten cryptids in a two-player " +
        "game, twelve in a three-player game, or sixteen in a four-player game.",
      "Most newspapers sold wins. A tie goes to whoever published more cryptids, and " +
        "if that is level too, the tie stands.",
    ],
    cards: [
      { src: "/cards/cryptid-files-bigfoot.webp", alt: "Bigfoot, a cryptid worth three newspapers" },
      { src: "/cards/cryptid-files-mothman.webp", alt: "Mothman, a cryptid worth four newspapers" },
      { src: "/cards/cryptid-files-skinwalker.webp", alt: "Skinwalker, a cryptid worth five newspapers" },
      { src: "/cards/cryptid-files-photograph.webp", alt: "Photograph, one of the eight kinds of evidence" },
      { src: "/cards/cryptid-files-tracks.webp", alt: "Tracks, one of the eight kinds of evidence" },
      { src: "/cards/cryptid-files-breaking-news.webp", alt: "Breaking News, an action card" },
    ],
  },
  {
    slug: "fish-tales",
    name: "Fish Tales",
    tagline: "Rig the right setup, land the legend, and out-lie the rest of the dock.",
    blurb:
      "Competitive angling for people who have never owned a rod. Every fish in the water " +
      "wants three particular pieces of tackle — the right bait, the right boat, the right " +
      "hour of the morning. Put the setup together and it goes in your cooler. Leave it a " +
      "turn too long and someone else's story card ruins the cast.",
    players: "2–4 players",
    length: "30–45 minutes",
    kicker: ["Cast a line", "Share a story", "Keep playing"],
    tag: "Storytelling",
    // Marked live on the user's say-so 2026-09-14: the module ships with
    // feat/livestream-platform, which reaches app.neoncrowgames.com the next
    // day. Deploy the app BEFORE this site, or the Play button leads to a
    // catalogue that has no such game.
    status: "live",
    cover: "/fish-tales-cover.webp",
    fan: "/fish-tales-fan.webp",
    accent: "#ffcc55",
    components: [
      {
        count: "20",
        label: "Fish",
        detail:
          "Muskie, sturgeon, alligator gar. Each names the three pieces of tackle that " +
          "will land it, and is worth two to five trophy points.",
      },
      {
        count: "8",
        label: "kinds of Tackle",
        detail:
          "Live minnows, lures, nightcrawlers, a bass boat, a jon boat, an expert cast, " +
          "and the two hours worth fishing — dawn and dusk.",
      },
      {
        count: "20",
        label: "Story cards",
        detail:
          "Squalls, flash floods, a rival's wake, and the fish finder that finds the one " +
          "piece of tackle you were missing.",
      },
    ],
    turn: [
      { step: "Refresh", detail: "An empty deck is rebuilt from its own discard pile." },
      { step: "Draw", detail: "One card, from either the Tackle deck or the Story deck." },
      {
        step: "Fish",
        detail:
          "Land a catch by spending the three pieces of tackle it calls for, or cut bait " +
          "and try for a better setup. One or the other.",
      },
      { step: "Refill the water", detail: "A landed fish leaves a gap; a new one swims into it." },
      { step: "Discard", detail: "Down to a hand of seven." },
    ],
    victory: [
      "Reach twenty trophy points and the game stops there — the highest total on the " +
        "dock wins.",
      "But if the supply runs dry first it is scored the other way round: the biggest " +
        "cooler wins, and a fatter score loses to one more fish.",
    ],
    cards: [
      { src: "/cards/fish-tales-muskie-king.webp", alt: "Muskie King, worth five trophy points" },
      { src: "/cards/fish-tales-alligator-gar.webp", alt: "Alligator Gar, which penalises its rivals" },
      { src: "/cards/fish-tales-flathead-catfish.webp", alt: "Flathead Catfish" },
      { src: "/cards/fish-tales-live-minnow.webp", alt: "Live Minnow, one of the eight kinds of tackle" },
      { src: "/cards/fish-tales-bass-boat.webp", alt: "Bass Boat, one of the eight kinds of tackle" },
      { src: "/cards/fish-tales-fish-finder.webp", alt: "Fish Finder, a story card" },
    ],
  },
  {
    slug: "triad-paranormal",
    name: "Triad Paranormal",
    tagline: "Three instruments say it is there. Document it, then get rid of it.",
    blurb:
      "Supernatural investigation for people who own too many cameras. Every ghost on " +
      "the street answers to exactly three pieces of equipment — an EMF reader, a cold " +
      "spot, the right lens in the dark. Put the triad together and it goes in your " +
      "logbook. Reach for it a turn too late and a rival has already banished it, or " +
      "worse, is holding the card that stops you.",
    players: "2–4 players",
    length: "30–45 minutes",
    kicker: ["People", "Places", "Paranormal"],
    tag: "Investigation",
    // Marked live 2026-09-16, after checking rather than assuming: the backend
    // was deployed (schema V33, module registered), the app bundle synced, and
    // the bundle app.neoncrowgames.com actually serves was confirmed to contain
    // the game — the check this field's contract above asks for.
    status: "live",
    cover: "/triad-paranormal-cover.webp",
    fan: "/triad-paranormal-fan.webp",
    accent: "#8ed6be",
    components: [
      {
        count: "20",
        label: "Ghosts",
        detail:
          "Shades, wraiths, a full body apparition. Each names the three pieces of gear " +
          "that will document it, and is worth two to five banish points.",
      },
      {
        count: "8",
        label: "kinds of Gear",
        detail:
          "EMF reader, digital voice recorder, thermometer, laser grid, spirit box, " +
          "REM pod, and the two cameras — thermal and night vision.",
      },
      {
        count: "30",
        label: "Haunt cards",
        detail:
          "Holy water that stands in for any instrument, a medium who finds the one you " +
          "are missing, and a possession that cancels a rival's banishment outright.",
      },
    ],
    turn: [
      { step: "Refresh", detail: "An empty deck is rebuilt from its own discard pile." },
      { step: "Draw", detail: "One card, from either the Gear deck or the Haunt deck." },
      {
        step: "Encounter",
        detail:
          "Banish a ghost by spending the three pieces of gear it calls for, or " +
          "investigate — swap any number of cards for fresh gear. One or the other.",
      },
      { step: "Refresh the field", detail: "A banished ghost leaves a gap; a new one drifts in." },
      { step: "Discard", detail: "Down to a hand of seven." },
    ],
    victory: [
      "Reach twenty banish points and the game stops there — the highest total wins.",
      "If the street empties first, it is scored the same way: whoever has the most " +
        "banish points takes it. Unlike the lake, there is no second measure.",
    ],
    cards: [
      { src: "/cards/triad-paranormal-glow-eyes.webp", alt: "Glow-Eyes, worth five banish points" },
      {
        src: "/cards/triad-paranormal-full-body-apparition.webp",
        alt: "Full Body Apparition, which takes a ghost out of every rival's logbook",
      },
      { src: "/cards/triad-paranormal-poltergeist.webp", alt: "Poltergeist, an ongoing logbook ability" },
      { src: "/cards/triad-paranormal-emf-reader.webp", alt: "EMF Reader, one of the eight kinds of gear" },
      { src: "/cards/triad-paranormal-spirit-box.webp", alt: "Spirit Box, one of the eight kinds of gear" },
      { src: "/cards/triad-paranormal-possession.webp", alt: "Possession, a haunt card that cancels a banishment" },
    ],
  },
  {
    slug: "war-rigs",
    name: "Architect: War Rigs",
    tagline: "Strip the armour off what they built. The hull underneath gives way.",
    blurb:
      "Two war rigs in a burning city. You pick a chassis, a pilot and an engine before " +
      "the first card is drawn, then spend the game bolting weapons onto hardpoints and " +
      "prying your opponent's off again. Nobody shoots a rig directly — you take its " +
      "armour plate by plate, and every attachment that falls costs a point of hull. " +
      "Push too hard and the heat does the job for them.",
    players: "2 players",
    // An estimate from the rules, not from a playtested table: ten to fifteen
    // hull, one to three hits to strip an attachment, four actions a turn.
    // Correct it the first time real people finish a game.
    length: "20–40 minutes",
    kicker: ["Build", "Aim", "Strip it down"],
    tag: "Head to head",
    // Marked live 2026-09-28 after the check this field's contract describes:
    // schema V35 applied in production, the module registered, the app bundle
    // synced, and index-CU6okHO3.js confirmed over the CDN to contain the game
    // before this line changed.
    status: "live",
    cover: "/war-rigs-cover.webp",
    fan: "/war-rigs-fan.webp",
    accent: "#ff5a3c",
    components: [
      {
        count: "9",
        label: "Rigs",
        detail:
          "The chassis everything bolts to. Each prints its hull, its heat threshold and " +
          "the hardpoints it has to fill — three software and one accessory on a light " +
          "frame, a third arm on the one built to carry it.",
      },
      {
        count: "9",
        label: "Pilots",
        detail:
          "Chosen before the game with the rig and the engine, and never drawn. One adds " +
          "a point to every shot, one sets the enemy on fire with each hit, one lets you " +
          "pay a cost in heat you were going to take anyway.",
      },
      {
        count: "6",
        label: "Engines",
        detail:
          "Where energy comes from: one cube at the start of your turn and two more if " +
          "you spend an action on it. Three run a single colour, three trade a cube for " +
          "an adaptive one that pays for anything.",
      },
      {
        count: "60",
        label: "cards in a Cache",
        detail:
          "Heads, armaments, mobility, accessories, software and tactics across three " +
          "corporations — Kazakov Korp, Ambermast and Stratum IV. Forty-card minimum, " +
          "three copies of any card, any colours you like.",
      },
      {
        count: "4",
        label: "Basic Attachments",
        detail:
          "A head, two arms and a set of legs every rig starts with. They cannot be " +
          "scrapped and they carry no armour, so a hit on a bare one goes straight to " +
          "the hull — cover them, and they stop being a target at all.",
      },
    ],
    turn: [
      { step: "Start of turn", detail: "Your engine makes a cube of energy." },
      { step: "Draw", detail: "One card off your Cache, free." },
      {
        step: "Cooldown",
        detail:
          "Over your heat threshold, scrap that many cards off the top of your Cache and " +
          "cool five. Under it you cool nothing, so heat carries.",
      },
      {
        step: "Four actions",
        detail:
          "Equip an attachment, draw, recover from a knockdown, run the engine, or use " +
          "an action printed on a card — which is how every attack is made.",
      },
      { step: "End of turn", detail: "Scrap down to a Reserve of six." },
    ],
    victory: [
      "Attacks target an attachment, never the rig. Both players roll a die, the attacker " +
        "wins ties, and a hit takes one armour plate off whatever was aimed at.",
      "At zero plates the attachment is scrapped and its owner's rig takes a point of hull. " +
        "A hit on an uncovered Basic Attachment skips the middle step and costs hull outright.",
      "Reduce the other rig to zero hull and you win. Heat can do it for you: run too hot for " +
        "too long and your own Cache runs out underneath you.",
    ],
    cards: [
      { src: "/cards/war-rigs-striga-class.webp", alt: "Striga - Class, a rig with a third armament hardpoint" },
      { src: "/cards/war-rigs-k-v-sunburn-komarov.webp", alt: "K.V. \"Sunburn\" Komarov, a pilot who sets the target on fire" },
      { src: "/cards/war-rigs-inertial-drive-stroker.webp", alt: "Inertial Drive \"Stroker\", a single-colour engine" },
      { src: "/cards/war-rigs-gatling-autocannon.webp", alt: "Gatling Autocannon, a ranged armament" },
      { src: "/cards/war-rigs-composite-plating.webp", alt: "Composite Plating, a head that shrugs off Penetrate" },
      { src: "/cards/war-rigs-fight-fire-with-fire.webp", alt: "Fight Fire With Fire, a tactic that heats every enemy rig" },
    ],
  },
  {
    slug: "heroes-bounties",
    name: "Heroes 3: Bounties",
    tagline: "Five bounties on the board, and every rival wants the same ones.",
    blurb:
      "A bounty race through a monster-haunted realm. Five monsters sit face up on the " +
      "Bounty Board and every one of them names the three heroes it will take to bring " +
      "it down — a wizard, a bard, a barbarian with nothing left to lose. Collect the " +
      "party and the bounty is yours; the dragons pay best, and the dragons bite back.",
    players: "2–4 players",
    // An estimate from the rules, not from a playtested table: one draw and
    // one adventure a turn, five to seven bounties to reach twenty coins.
    // Correct it the first time real people finish a game.
    length: "20–40 minutes",
    kicker: ["Recruit", "Hunt", "Collect"],
    tag: "Bounty race",
    // Marked live 2026-10-01 after the check this field's contract describes:
    // schema V40 applied in production, the module registered, the app bundle
    // synced, and the bundle app.neoncrowgames.com serves confirmed over the
    // CDN to contain the game before this line changed.
    status: "live",
    cover: "/heroes-bounties-cover.webp",
    fan: "/heroes-bounties-fan.webp",
    accent: "#f0b84a",
    components: [
      {
        count: "20",
        label: "Monsters",
        detail:
          "Goblins and rat swarms up to an ancient vampire and a fire drake. Each names " +
          "the three hero classes that defeat it and pays two to five bounty coins — " +
          "and the big ones charge extra, or charge you afterwards.",
      },
      {
        count: "8",
        label: "Hero classes",
        detail:
          "Wizard, bard, rogue, druid, ranger, barbarian, paladin and cleric — sixty " +
          "cards in all. The classes the cheap bounties want are common; the ones the " +
          "dragons demand are not.",
      },
      {
        count: "28",
        label: "Action cards",
        detail:
          "A quick draw, a righteous zeal that fetches exactly the hero you are missing, " +
          "a bardic trance that ends a rival's turn as they draw, and the counter spell " +
          "that unmakes any of them.",
      },
    ],
    turn: [
      { step: "Start of turn", detail: "Monsters in your Trophy Hall wake up first." },
      { step: "Draw", detail: "One card, from either the Hero deck or the Action deck." },
      {
        step: "Adventure",
        detail:
          "Defeat a monster by spending the three heroes it names, or recruit — swap " +
          "any number of cards for fresh heroes. One or the other.",
      },
      { step: "Refresh the board", detail: "A claimed bounty leaves a gap; a new monster fills it." },
      { step: "Discard", detail: "Down to a hand of seven." },
    ],
    victory: [
      "Reach twenty bounty coins and the game stops there — the hunt is won on the spot.",
      "If the board and the deck both empty first, it is scored the same way: whoever " +
        "holds the most bounty coins takes it. Fewer, richer trophies beat a crowded hall.",
    ],
    cards: [
      { src: "/cards/heroes-bounties-fire-drake.webp", alt: "Fire Drake, a five-coin bounty that burns every hand at the table" },
      { src: "/cards/heroes-bounties-harpy-siren.webp", alt: "Harpy Siren, who taxes every trophy hall on her way down" },
      { src: "/cards/heroes-bounties-lizardman-scout.webp", alt: "Lizardman Scout, an ongoing trophy hall ability" },
      { src: "/cards/heroes-bounties-wizard.webp", alt: "Wizard, one of the eight hero classes" },
      { src: "/cards/heroes-bounties-barbarian.webp", alt: "Barbarian, one of the six-copy classes the big bounties demand" },
      { src: "/cards/heroes-bounties-counter-spell.webp", alt: "Counter Spell, which negates the card beneath it on the timing chain" },
    ],
  },
];

export const gameBySlug = (slug: string) => GAMES.find((g) => g.slug === slug);

/**
 * What all five games share, said once. The timing chain is the rule that
 * makes any of them feel like more than solitaire, so it is worth a paragraph
 * of its own on How to Play.
 */
export const SHARED_RULES = {
  title: "The timing chain",
  body:
    "None of these games waits politely for your turn. Action cards in Cryptid Files " +
    "and Heroes 3: Bounties, Story cards in Fish Tales, Haunt cards in Triad Paranormal " +
    "and Tactics in War Rigs all go onto a shared timing chain, and anyone who is " +
    "allowed to answer may add to it. Once nobody has anything left to add, the chain " +
    "resolves backwards — last card played, first card to happen. That is why a " +
    "publication can be fact-checked out of existence, why a fish already in the net " +
    "can still get away, why a ghost you have already paid for can stay exactly where " +
    "it is, why a shot you were certain of can end up hitting the wrong arm, and why a " +
    "counter spell can unmake the counter spell aimed at you.",
};
